import * as React from "react";
import { useCallback, useMemo } from "react";
import { Combobox } from "@/components/ui";
import { PlayersServices } from "@/services/bedrock";
import { usePaginatedCombobox } from "@/utils";
import { BuilderState, SpecificPlayer } from "../useFormRouteBuilder";
import { ChoiceCards, Helper, RouteCard, STEP_IDS } from "./RouteCard";

export interface PlayerOption {
  id: number;
  label: string;
  player: SpecificPlayer;
}

export function toPlayerOption(player: SpecificPlayer): PlayerOption {
  return { id: player.id, label: `${player.fullName || "No Name"} - ${player.email}`, player };
}

/** Player combobox state over `PlayersServices.getPlayers`, shared by the settings card and the tester. */
export function usePlayerOptions(initial: SpecificPlayer | null) {
  const fetchPlayers = useCallback(async (search: string | undefined, page: number) => {
    const result = await PlayersServices.getPlayers({ search, page, perPage: 25 });
    return {
      ...result,
      content: result.content.map(p =>
        toPlayerOption({ id: p.id, email: p.email ?? "", fullName: p.fullName ?? null })
      ),
    };
  }, []);
  const initialValue = useMemo(() => (initial ? toPlayerOption(initial) : null), [initial?.id]);
  return usePaginatedCombobox(fetchPlayers, false, initialValue);
}

interface Props {
  state: BuilderState;
  onChange: (patch: Partial<BuilderState>) => void;
}

export function FormCardWhoAsks({ state, onChange }: Props) {
  const players = usePlayerOptions(state.specificPlayer);
  const senderDescription = {
    totalexpert: "TotalExpert fills in the sender's email. We match it to the player.",
    crm: "Your CRM fills in the sender's email. We match it to the player.",
    website: "Your page passes the loan officer's email or alias.",
  }[state.destination];

  return (
    <RouteCard step={2} id={STEP_IDS.player} title="Who's asking" description="The player the review belongs to.">
      <ChoiceCards
        horizontal
        label="Who's asking"
        value={state.playerMode}
        onChange={playerMode => onChange({ playerMode })}
        options={[
          {
            value: "sender",
            label: state.destination === "website" ? "Whoever the page is for" : "Whoever sends the email",
            description: senderDescription,
          },
          {
            value: "specific",
            label: "One specific player",
            description: "Every click goes to this player. Update the route if they leave.",
          },
          { value: "none", label: "No player", description: "The form belongs to the account, not a person." },
        ]}
      />
      {state.playerMode === "specific" && (
        <Combobox
          {...players}
          onValueChange={(option: PlayerOption | null, details: unknown) => {
            players.onValueChange(option, details);
            onChange({ specificPlayer: option?.player ?? null });
          }}
          placeholder="Search for a player..."
          className="w-full"
        />
      )}
      <Helper>
        We find the player by email, alias or internal ID. That's who gets the review and the notification. With no
        match, the response arrives with no player.
      </Helper>
    </RouteCard>
  );
}
